/**
 * ActionMenu — generischer Dropdown-Menü-Button (Trigger + role=menu).
 *
 * Mechanik gespiegelt von CardActions (früher auch DownloadDropdown) (patterns/react.md §
 * close-menu-before-action): Esc + Outside-Click schließen, Autofocus aufs
 * erste Item nach Open, role=menu/menuitem. KEIN Arrow-Key-Roving (wie die
 * bestehenden Menüs — Tab + Esc reichen für das interne Tool).
 *
 * Genutzt für (a) den „+ Bilder hinzufügen ▾"-Picker (Mediathek vs Hochladen)
 * und (b) das „⋯"-Overflow-Menü (Duplizieren/Löschen) auf den Detailseiten.
 * Trigger nutzt das Button-Primitive (konsistentes Styling/Variants).
 *
 * `disabled` + `hint` (share-links-copy-extend PR B): ein deaktivierter Punkt
 * bleibt sichtbar und fokussierbar (`aria-disabled="true"`, kein natives
 * `disabled` — sonst übersprünge die Tastatur ihn und der Grund bliebe
 * ungelesen), ruft `onSelect` aber nie auf und lässt das Menü offen. Der
 * Hinweis steht als zweite Zeile darunter (13 px, S2-Untergrenze) und ist per
 * `aria-describedby` angebunden; der zugängliche Name bleibt das Label.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button, type ButtonSize, type ButtonVariant } from './Button';
import { ButtonInverse } from './ButtonInverse';

export interface ActionMenuItem {
  label: string;
  onSelect: () => void;
  icon?: ReactNode;
  /** Rot rendern (destruktiv, z.B. Löschen). */
  danger?: boolean;
  /** Sichtbar, aber nicht auslösbar (`aria-disabled`, `onSelect` wird nie gerufen). */
  disabled?: boolean;
  /** Zweite Zeile unter dem Label, z.B. warum der Punkt deaktiviert ist. */
  hint?: string;
}

interface ActionMenuProps {
  /** Trigger-Inhalt. Bei icon-only Trigger (z.B. ⋯) hier das Icon + `ariaLabel` setzen. */
  label: ReactNode;
  items: ActionMenuItem[];
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** Pflicht bei icon-only Trigger (Screenreader-Name). */
  ariaLabel?: string;
  /** Menü-Ausrichtung relativ zum Trigger. Default 'right'. */
  align?: 'left' | 'right';
  /** Additive Utilities auf den Trigger (Breite/Margin). */
  className?: string;
  /** Öffnungsrichtung. Default 'bottom'; 'top' für Trigger am unteren Rand (BulkActionBar). */
  side?: 'bottom' | 'top';
  /** Trigger auf dunkler Fläche → `ButtonInverse` (ghost) statt `Button`. */
  inverse?: boolean;
}

export function ActionMenu({
  label,
  items,
  variant = 'secondary',
  size = 'sm',
  leadingIcon,
  trailingIcon,
  ariaLabel,
  align = 'right',
  className,
  side = 'bottom',
  inverse = false,
}: ActionMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const hintIdBase = useId();

  // Esc + Outside-Click → schließen (mirror CardActions).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    function onClick(e: MouseEvent): void {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  // Autofocus aufs erste Item nach Open (50ms, damit Render committet).
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => firstItemRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [open]);

  function handleSelect(item: ActionMenuItem): void {
    // Deaktiviert: nichts auslösen, Menü offen lassen (der Hinweis bleibt lesbar).
    if (item.disabled) return;
    // close-menu-before-action: erst schließen, dann die (ggf. modal-öffnende)
    // Aktion — sonst kollidiert der Outside-Click-Handler mit dem neuen Modal.
    setOpen(false);
    item.onSelect();
  }

  return (
    <div className="relative">
      {inverse ? (
        <ButtonInverse
          ref={triggerRef}
          variant="secondary"
          size={size}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={ariaLabel}
          onClick={() => setOpen((v) => !v)}
          className={className}
        >
          {leadingIcon}
          {label}
          {trailingIcon}
        </ButtonInverse>
      ) : (
        <Button
          ref={triggerRef}
          variant={variant}
          size={size}
          leadingIcon={leadingIcon}
          trailingIcon={trailingIcon}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={ariaLabel}
          onClick={() => setOpen((v) => !v)}
          className={className}
        >
          {label}
        </Button>
      )}
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={ariaLabel}
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} ${
            side === 'top'
              ? `bottom-full mb-1.5 ${align === 'right' ? 'origin-bottom-right' : 'origin-bottom-left'}`
              : `top-full mt-1.5 ${align === 'right' ? 'origin-top-right' : 'origin-top-left'}`
          } z-30 min-w-[12rem] bg-white rounded-lg shadow-pop border border-ink-100 py-1 gf-pop-in`}
        >
          {items.map((item, i) => {
            const hintId = item.hint ? `${hintIdBase}-hint-${i}` : undefined;
            return (
              <button
                key={item.label}
                ref={i === 0 ? firstItemRef : undefined}
                type="button"
                role="menuitem"
                aria-disabled={item.disabled ? 'true' : undefined}
                // Mit Hinweis: Name = Label, Hinweis = Beschreibung (sonst flösse
                // die zweite Zeile in den zugänglichen Namen).
                aria-label={item.hint ? item.label : undefined}
                aria-describedby={hintId}
                onClick={() => handleSelect(item)}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors focus:bg-ink-50 focus:outline-none ${
                  item.disabled
                    ? 'cursor-not-allowed text-ink-400'
                    : `hover:bg-ink-50 ${item.danger ? 'text-danger-700' : 'text-brand-green-800'}`
                }`}
              >
                {item.icon && (
                  <span
                    className={`flex-shrink-0${item.hint ? ' self-start pt-0.5' : ''}`}
                    aria-hidden="true"
                  >
                    {item.icon}
                  </span>
                )}
                {item.hint ? (
                  <span className="flex flex-col">
                    <span>{item.label}</span>
                    <span id={hintId} className="text-xs text-ink-500">
                      {item.hint}
                    </span>
                  </span>
                ) : (
                  <span>{item.label}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
